import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import {
	useCreateRefund,
	useForceCheck,
	usePaymentDetail,
} from "@/hooks/use-payments";
import { fmtFCFA } from "@/lib/format";
import { statusPayment, statusRefund } from "@/lib/status";

interface InfoItemProps {
	label: string;
	value: React.ReactNode;
	mono?: boolean;
}

function InfoItem({ label, value, mono = true }: InfoItemProps) {
	return (
		<div>
			<div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
				{label}
			</div>
			<div className={mono ? "mono" : undefined} style={{ fontSize: 13 }}>
				{value ?? "—"}
			</div>
		</div>
	);
}

export function AdminPaymentDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { data, isLoading, refetch } = usePaymentDetail(id ?? null);
	const forceCheck = useForceCheck();
	const createRefund = useCreateRefund();

	const [refundOpen, setRefundOpen] = useState(false);
	const [refundAmount, setRefundAmount] = useState("");
	const [refundReason, setRefundReason] = useState("order_cancelled");
	const [refundText, setRefundText] = useState("");

	if (isLoading || !data) {
		return (
			<>
				<PageHead title="Paiement" sub="Chargement…" />
			</>
		);
	}

	const { payment, attempts, refunds, webhooks } = data;
	const s = statusPayment(payment.status);

	const submitRefund = async () => {
		const amount = parseInt(refundAmount, 10);
		if (!amount || amount <= 0) return alert("Montant invalide.");
		try {
			await createRefund.mutateAsync({
				paymentId: payment.id,
				amount,
				reasonCode: refundReason,
				reasonText: refundText || undefined,
				idempotencyKey: `${payment.id}-${Date.now()}`,
			});
			setRefundOpen(false);
			setRefundAmount("");
			setRefundText("");
			refetch();
		} catch (e) {
			const err = e as {
				response?: { data?: { message?: string } };
				message: string;
			};
			alert(err.response?.data?.message ?? err.message);
		}
	};

	return (
		<>
			<PageHead
				title={`Paiement ${payment.reference}`}
				sub={`${payment.method} · ${payment.provider?.label ?? "—"} · ${fmtFCFA(payment.amount_gross)}`}
				actions={
					<>
						<AdminButton variant="ghost" onClick={() => navigate("/paiements")}>
							<AdminIcon n="chevL" s={14} />
							Retour
						</AdminButton>
						<AdminButton
							variant="ghost"
							onClick={() =>
								forceCheck.mutate(payment.id, {
									onSuccess: () => refetch(),
									onError: (e) => {
										const err = e as {
											response?: { data?: { message?: string } };
											message: string;
										};
										alert(err.response?.data?.message ?? err.message);
									},
								})
							}
							disabled={forceCheck.isPending}
						>
							<AdminIcon n="refresh" s={14} />
							Force-check PSP
						</AdminButton>
						{["SUCCESS", "PARTIALLY_REFUNDED", "CAPTURED"].includes(
							payment.status,
						) && (
							<AdminButton
								variant="primary"
								onClick={() => setRefundOpen((o) => !o)}
							>
								<AdminIcon n="download" s={14} />
								Rembourser
							</AdminButton>
						)}
					</>
				}
			/>

			<div className="kpi-grid">
				<div className="kpi">
					<div className="kpi-l">Statut</div>
					<div className="kpi-v">
						<Tag variant={s.cls}>{s.l}</Tag>
					</div>
				</div>
				<div className="kpi success">
					<div className="kpi-l">Brut</div>
					<div className="kpi-v tn">{fmtFCFA(payment.amount_gross)}</div>
					<span className="kpi-delta neutral">
						PSP {fmtFCFA(payment.fee_provider)}
					</span>
				</div>
				<div className="kpi accent">
					<div className="kpi-l">Net</div>
					<div className="kpi-v tn">{fmtFCFA(payment.amount_net)}</div>
					<span className="kpi-delta neutral">
						Plateforme {fmtFCFA(payment.fee_platform)}
					</span>
				</div>
				<div className="kpi">
					<div className="kpi-l">Initiée</div>
					<div className="kpi-v" style={{ fontSize: 14 }}>
						{new Date(payment.initiated_at).toLocaleString("fr-FR")}
					</div>
					{payment.confirmed_at && (
						<span className="kpi-delta up">
							Confirmée {new Date(payment.confirmed_at).toLocaleString("fr-FR")}
						</span>
					)}
				</div>
			</div>

			{refundOpen && (
				<Card style={{ marginBottom: 16 }}>
					<CardHead
						title="Initier un remboursement"
						sub="Selon le montant : auto-approuvé ≤ 50 k, validation 4-eyes au-delà."
					/>
					<CardBody>
						<div
							style={{
								display: "grid",
								gap: 0,
								gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
								columnGap: 16,
							}}
						>
							<FormRow label="Montant (FCFA)" required>
								<input
									type="number"
									className="aform-i"
									min={1}
									max={payment.amount_gross}
									value={refundAmount}
									onChange={(e) => setRefundAmount(e.target.value)}
									placeholder={fmtFCFA(payment.amount_gross)}
								/>
							</FormRow>
							<FormRow label="Motif" required>
								<select
									className="aform-i"
									value={refundReason}
									onChange={(e) => setRefundReason(e.target.value)}
								>
									<option value="order_cancelled">Commande annulée</option>
									<option value="return_validated">Retour validé</option>
									<option value="goodwill">Geste commercial</option>
									<option value="duplicate">Doublon</option>
									<option value="fraud">Fraude</option>
									<option value="other">Autre</option>
								</select>
							</FormRow>
						</div>
						<FormRow
							label="Note"
							helper={
								refundReason === "other"
									? "Obligatoire ≥ 5 caractères"
									: "Optionnel"
							}
						>
							<textarea
								className="aform-i"
								rows={2}
								value={refundText}
								onChange={(e) => setRefundText(e.target.value)}
								placeholder="Contexte / référence dossier client…"
							/>
						</FormRow>
						<div
							style={{
								display: "flex",
								gap: 8,
								justifyContent: "flex-end",
								marginTop: 8,
							}}
						>
							<AdminButton variant="ghost" onClick={() => setRefundOpen(false)}>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								onClick={submitRefund}
								disabled={createRefund.isPending}
							>
								Soumettre
							</AdminButton>
						</div>
					</CardBody>
				</Card>
			)}

			<Card style={{ marginBottom: 16 }}>
				<CardHead title="Informations" />
				<CardBody>
					<div
						style={{
							display: "grid",
							gap: 16,
							gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
						}}
					>
						<InfoItem label="Référence interne" value={payment.reference} />
						<InfoItem label="ID PSP" value={payment.provider_reference} />
						<InfoItem label="Commande" value={payment.order_id} />
						<InfoItem label="MSISDN payeur" value={payment.payer_msisdn} />
						<InfoItem label="Code d'échec" value={payment.failure_code} />
						<InfoItem
							label="Message"
							value={payment.failure_message}
							mono={false}
						/>
					</div>
				</CardBody>
			</Card>

			<Card style={{ marginBottom: 16 }}>
				<CardHead
					title="Timeline Webhooks"
					sub={`${webhooks.length} événement(s) reçu(s)`}
				/>
				<CardBody>
					{webhooks.length === 0 && (
						<div className="empty">Aucun événement webhook reçu.</div>
					)}
					{webhooks.length > 0 && (
						<ol style={{ paddingLeft: 16, display: "grid", gap: 8, margin: 0 }}>
							{webhooks.map((w) => (
								<li key={w.id} style={{ fontSize: 13 }}>
									<strong className="mono">{w.event_type ?? "—"}</strong>{" "}
									<span className="muted">· {w.provider} ·</span>{" "}
									<span className="mono muted">{w.event_id}</span>{" "}
									<span className="muted">·</span>{" "}
									{new Date(w.received_at).toLocaleString("fr-FR")}
									{w.processed_at && <span className="muted"> · traité</span>}
								</li>
							))}
						</ol>
					)}
				</CardBody>
			</Card>

			<Card style={{ marginBottom: 16 }}>
				<CardHead title="Tentatives" sub={`${attempts.length} tentative(s)`} />
				{attempts.length === 0 ? (
					<CardBody>
						<div className="empty">Aucune tentative enregistrée.</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>#</th>
									<th>Statut</th>
									<th>Latence (ms)</th>
									<th>Début</th>
									<th>Fin</th>
								</tr>
							</thead>
							<tbody>
								{attempts.map((a) => (
									<tr key={a.id}>
										<td className="mono">{a.attempt_no}</td>
										<td>{a.status}</td>
										<td className="right">{a.latency_ms ?? "—"}</td>
										<td>{new Date(a.started_at).toLocaleString("fr-FR")}</td>
										<td>
											{a.ended_at
												? new Date(a.ended_at).toLocaleString("fr-FR")
												: "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>

			<Card>
				<CardHead
					title="Remboursements"
					sub={`${refunds.length} remboursement(s)`}
				/>
				{refunds.length === 0 ? (
					<CardBody>
						<div className="empty">Aucun remboursement.</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Référence</th>
									<th className="right">Montant</th>
									<th>Motif</th>
									<th>Statut</th>
									<th>Demandé</th>
								</tr>
							</thead>
							<tbody>
								{refunds.map((r) => {
									const rs = statusRefund(r.status);
									return (
										<tr key={r.id}>
											<td className="mono">{r.reference}</td>
											<td className="right tn">
												<strong>{fmtFCFA(r.amount)}</strong>
											</td>
											<td>{r.reasonCode}</td>
											<td>
												<Tag variant={rs.cls}>{rs.l}</Tag>
											</td>
											<td className="muted">
												{new Date(r.requestedAt).toLocaleString("fr-FR")}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</>
	);
}
